"use client";

import { ArrowDown, ArrowUp } from "lucide-react";

import { computeMovement } from "@/components/odds-screen/line-movement-hook";
import type {
  LineHistoryPoint,
  LineHistorySeries,
} from "@/components/odds/line-sparkline";
import { cn } from "@/lib/utils";
import { DFS_BOOKS } from "@/lib/whale-watch/whale-watch-shared";


const WINDOWS = [
  { ms: 3_600_000, label: "1h" },
  { ms: 6 * 3_600_000, label: "6h" },
  { ms: 24 * 3_600_000, label: "24h" },
] as const;

const LINE_EPS = 0.001;

export interface GlanceDelta {
  direction: "up" | "down";
  delta: number;
  windowLabel: string;
}

export function computeGlanceDelta(
  points: LineHistoryPoint[] | null | undefined,
  now: number = Date.now(),
): GlanceDelta | null {
  if (!points || points.length < 2) {
    return null;
  }
  const live = points
    .filter((p) => p.change_kind !== "removed")
    .map((p) => ({ ms: Date.parse(p.ts), line: p.line, price: p.price_american }))
    .filter((p) => Number.isFinite(p.ms))
    .sort((a, b) => a.ms - b.ms);
  if (live.length < 2) {
    return null;
  }
  const latest = live[live.length - 1];

  for (const window of WINDOWS) {
    const boundary = now - window.ms;
    let anchorIdx = -1;
    for (let i = 0; i < live.length && live[i].ms <= boundary; i++) {
      anchorIdx = i;
    }
    if (anchorIdx < 0 || anchorIdx >= live.length - 1) {
      continue;
    }
    const flat = live
      .slice(anchorIdx)
      .every((p) => Math.abs(p.line - latest.line) < LINE_EPS);
    if (!flat) {
      continue;
    }
    const move = computeMovement(live[anchorIdx].price, latest.price);
    if (move.direction === "none") {
      continue;
    }
    return {
      direction: move.direction,
      delta: move.delta,
      windowLabel: window.label,
    };
  }
  return null;
}

export function MovementDelta({
  series,
  className,
}: {
  series: LineHistorySeries | null | undefined;
  className?: string;
}) {
  if (!series || DFS_BOOKS.has(series.book.toLowerCase())) {
    return null;
  }
  const delta = computeGlanceDelta(series.points);
  if (!delta) {
    return null;
  }
  const Arrow = delta.direction === "up" ? ArrowUp : ArrowDown;
  const label = `Best price moved ${delta.delta} ${
    delta.direction === "up" ? "toward" : "away from"
  } you over the last ${delta.windowLabel}`;
  return (
    <span
      aria-label={label}
      title={label}
      className={cn(
        "inline-flex items-center gap-0.5 data-num text-[10px] tabular-nums text-muted-foreground/65",
        className,
      )}
    >
      <Arrow
        aria-hidden
        className={cn(
          "size-2.5",
          delta.direction === "up" ? "text-accent-value/60" : "text-red-400/55",
        )}
      />
      {delta.delta}
      <span className="text-muted-foreground/35">·</span>
      {delta.windowLabel}
    </span>
  );
}
